import React, { Fragment, useEffect } from 'react';
import styled from '@emotion/styled';
import moment from 'moment';
import { Link } from 'react-router-dom';
import numeral from 'numeral';

import FixedPanel from '../../common/FixedPanel';
import Button from '../../common/Button';
import PageTitle from '../../common/PageTitle';
import Pagination from '../../common/Pagination';
import SpinnerBlock from '../../common/SpinnerBlock';
import Checkbox from '../../common/Checkbox';

import StatusBar from '../../common/StatusBar';
import FiltersRow from '../MainPage/FiltersRow';

import { DELIVERY_TYPE_TRANSLATE, SUBSCRIPTIONS_PAGE_TABS, TAG_COLORS } from '../../../config';

const Wrapper = styled('div')`
  position: relative;

  .thead-light th {
    font-weight: 600;
    border-bottom-color: rgb(222, 226, 230) !important;
    border-bottom-style: solid !important;
    border-bottom-width: 2px !important;
  }

  .order-id-title {
    font-weight: 700;
  }

  .delivery-logo {
    height: 35px;
    background-repeat: no-repeat;
    background-position: left center;
    background-size: contain;
  }

  .fixed-table-body {
    overflow-x: auto;
  }

  tr {
    position: relative;

    .custom-checkbox {
      margin-left: 10px;
    }
  }

  td {
    position: relative;
  }

  tr:hover {
    background-color: #f2f6f9;

    .help-tooltip {
      display: flex;
      left: 0;
      top: 60px;
    }
  }

  .help-tooltip-wrapper {
    position: absolute;
  }

  tr.canceled {
    filter: opacity(65%) grayscale(100%);
  }

  .name {
    max-width: 180px;
  }

  .hover-tooltip-wrapper {
    cursor: pointer;
    position: relative;
    .hover-tooltip {
      display: none;
      position: absolute;
      top: 25px;
      font-size: 11px;
      background-color: #fff;
      border: 1px solid #dee2e6;
      padding: 10px 12px;
      border-radius: 8px;
      z-index: 1;
    }
    &:hover {
      .hover-tooltip {
        display: block;
      }
    }
  }

  .mark-container {
    position: absolute;
    left: 0;
    top: 5px;
    bottom: 5px;
    padding: 4px;

    .mark-wrapper {
      width: 4px;
      height: 100%;
      background: transparent;
      border-radius: 4px;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      pointer-events: none;

      .mark {
        flex: 1 1 auto;
        background: transparent;
        margin-bottom: 1px;

        &:last-of-type {
          margin-bottom: 0;
        }
      }
    }
  }

  .marks-list-wrapper {
    position: absolute;
    width: 165px;
    min-height: calc(100% - 8px);
    border-radius: 3px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    top: 4px;
    left: 4px;
    opacity: 0;
    z-index: -1;
    transition: opacity 0.3s;
    pointer-events: none;

    .add-mark {
      flex: 1 0 auto;
      width: 100%;
      border: none;
      padding: 0 12px;
      text-align: left;
      color: #7e57c2;
      background: #f5f8fa;

      i {
        margin-right: 4px;
      }
    }

    .tag {
      line-height: 15px;
      font-size: 11px;
      padding: 5px 10px;
      border-bottom: 1px solid white;
      color: #fffafa;
      display: block;
      margin: 0;
      flex: 1 0 auto;
      display: flex;
      align-items: center;
      cursor: pointer;

      :last-of-type {
        border-bottom: none;
      }

      :not(:hover) i {
        display: none;
      }

      i {
        width: 8px;
        height: 8px;
        margin-left: auto;
        margin-top: -2px;
      }
    }
  }

  .mark-container:hover .marks-list-wrapper {
    opacity: 1;
    z-index: 1;
    pointer-events: all;
    cursor: pointer;
  }

  tr:hover .mark-wrapper {
    background: #e8edf0;
    pointer-events: all;
    cursor: pointer;
  }
`;

const OrdersTabsWrapper = styled('div')`
  .nav-tabs-orders {
    position: absolute;
    top: -38px;
  }

  .nav-tabs-orders .nav-item.show .nav-link,
  .nav-tabs-orders .nav-link.active {
    color: #7e57c2;
    background-color: #fff;
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.1);
  }

  .nav-tabs-orders .nav-link-after {
    content: '';

    left: 0px;
    width: 100%;
    height: 5px;
    background-color: #fff;
    border: none;
  }

  .nav-tabs-orders .nav-link-after {
    border-top: 1px solid #edf0f2;

    margin-top: -1px;
  }

  .nav-tabs-orders .nav-link-after.active {
    border-top: 0px;
    margin-top: 0px;
  }

  .nav-tabs-orders .nav-link.active .badge {
    background-color: #7e57c2;
    color: #fff;
  }

  .nav-tabs-orders .nav-link .badge {
    background-color: #dfe4e6;
    color: #828489;
    margin-left: 10px;
    font-weight: 600;
  }

  .nav-tabs-orders .nav-link .badge {
  }

  .nav-tabs-orders .nav-item {
    margin-right: 4px;
  }

  .nav-tabs-orders .nav-link {
    border: 1px solid transparent;
    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;
    background-color: #fbfcfd;
  }

  .nav-tabs-orders .nav-link.active {
    border: 1px solid transparent;
  }

  .nav-tabs-orders .nav-link:hover {
    background-color: #fff;
  }

  .nav-tabs-orders > li > a {
    color: #7a7c82;
    font-weight: 600;
  }

  .nav-link {
    display: block;
    padding: 0.5rem 1rem;
    cursor: pointer;
  }

  .nav-link .nav-link-icon {
    padding-top: 1px;
    margin-right: 3px;
  }

  .nav-link i {
    font-size: 1rem;
    margin-right: 3px;
    margin-top: 4px;
  }

  .extra-content {
    display: flex;

    .tags-button {
      margin-right: auto;
    }

    .unpacking-button {
      margin-left: 0 !important;
    }
  }

  @media (max-width: 1205px) {
    .tab-title {
      display: none;
    }
  }

  @media (max-width: 520px) {
    .nav-link {
      padding-right: 5px;
      padding-left: 5px;
      .badge {
        display: none;
      }
    }
  }
`;

const OrdersCardWrapper = styled('div')`
  .card {
    margin-top: 38px;
    border-top-left-radius: 0px;
  }

  .form-wrapper {
    display: flex;

    form {
      flex-grow: 1;
    }
  }

  .tags-button {
    margin-bottom: 12px;
    margin-left: -12px;
    align-items: center;
    display: flex;
  }

  .tag-point {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-left: 3px;
  }

  .orders-search-group {
    position: relative;
    width: 100%;
  }

  .orders-search-group input {
    width: 100%;
  }

  .orders-search-group button {
    position: absolute;
    top: 5px;
    right: 5px;
  }

  .unpacking-button {
    &:focus {
      background-color: #f1f5f7;
      border-color: #f1f5f7;
      box-shadow: none !important;
    }
    .badge-pill {
      background-color: #7e57bf;
      color: #fff;
      font-weight: 600;
      margin-left: 5px;
    }
  }

  .shipping-button {
    .badge-pill {
      background-color: #fff;
      color: #1bbc9b;
      font-weight: 600;
      margin-left: 5px;
    }
  }

  .filters-row {
    > div:last-child {
      padding-right: 0;
    }
  }

  .reserved {
    padding: 5px 25px 5px 10px;
    background: #f5f5f5;
    borderradius: 5px;
    margin: 10px 0 0;
    position: relative;

    i {
      margin-left: 5px;
      position: absolute;
      top: 5px;
      right: 5px;
      cursor: pointer;
    }
  }
`;

const DeliveryBlock = (props) => {
  let delivery_title;

  if (DELIVERY_TYPE_TRANSLATE.hasOwnProperty(props.row.delivery.type)) {
    if (!props.row.delivery.title || props.row.delivery.type === 'SELF_PICKUP') {
      delivery_title = DELIVERY_TYPE_TRANSLATE[props.row.delivery.type];
    } else {
      delivery_title =
        props.row.delivery.title + ' – ' + DELIVERY_TYPE_TRANSLATE[props.row.delivery.type];
    }
  } else {
    delivery_title = '';
  }

  return (
    <div className="d-flex flex-column">
      <div
        className="delivery-logo"
        style={{ backgroundImage: `url(${props.row.delivery.logoUrl})` }}
      />
      <div>
        <small className="text-muted">{delivery_title}</small>
      </div>
    </div>
  );
};

const getOrderDateString = (date) => {
  const currentMoment = moment(date);
  let format = 'DD MMM';
  if (moment().diff(currentMoment, 'months') > 6) {
    format += ' YYYY';
  }
  format += ', HH:mm';
  return moment(date).format(format);
};

const DesktopLayout = (props) => {
  const {
    selectedLength,
    clearSelected,
    loadingCopy,
    handleCopySelected,
    ActionsButton,
    loadingActions,
    handleDeleteSelected,
    showFixedPanelAgreement,
    loadingConfirm,
    handleConfirmSelected,
    showFixedPanelEquipment,
    loadingPackaging,
    handlePackagingSelected,
    activeTab,
    handleOpenTab,
    statusesLoading,
    getTabOrdersLength,
    statusesLength,
    orders,
    search,
    selected,
    showFilter,
    unpackingOrders,
    unpackingFilter,
    packingControlOrders,
    packingControlFilter,
    loadingReport,
    warehouses,
    activeWarehouse,
    setActiveWarehouse,
    handleSearch,
    setShowFilter,
    handleCloseFilter,
    handleSetFilters,
    handleSetUnpackingFilter,
    handleSetControlPacking,
    handleShippingOrders,
    handleNewOrder,
    handleGetReport,
    updateOrders,
    updateStatuses,
    history,
    filterFormInitial,
    setFilterFormInitial,
    loading,
    filters,
    handleToggleMainCheckbox,
    selectedAll,
    THEAD_ITEMS,
    filteredOrders,
    handleToggleCheckbox,
    urlSearchStr,
    handleAddTag,
    scrollPosition,
    ReceiverBlock,
    LocationBlock,
    PaymentInfoBlock,
    currencySymbol,
    totalItems,
    page,
    handlePageSelect,
    setMobileCustomMenu,
  } = props;

  useEffect(() => {
    setMobileCustomMenu(null);
  }, [setMobileCustomMenu]);

  return (
    <>
      <FixedPanel show={selectedLength > 0}>
        <div className="fixed-panel-inner">
          {selectedLength ? (
            <div className="mr-3">
              <div>Выбрано заказов: {selectedLength}</div>
              <div className="clear-selected">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    clearSelected();
                  }}
                >
                  Снять выделение
                </a>
              </div>
            </div>
          ) : null}
          <div className="buttons-block">
            <Button
              className="mr-2"
              classType="btn-light"
              text="Копировать"
              icon="fe-copy"
              disabled={loadingCopy}
              loading={loadingCopy}
              onClick={handleCopySelected}
            />
            <ActionsButton
              loadingActions={loadingActions}
              handleDeleteSelected={handleDeleteSelected}
            />
            {showFixedPanelAgreement ? (
              <Button
                className="mr-2"
                text="Подтвердить"
                disabled={loadingConfirm}
                loading={loadingConfirm}
                onClick={handleConfirmSelected}
              />
            ) : null}
            {showFixedPanelEquipment ? (
              <Button
                className="mr-2"
                text="На упаковку"
                disabled={loadingPackaging}
                loading={loadingPackaging}
                onClick={handlePackagingSelected}
              />
            ) : null}
          </div>
        </div>
      </FixedPanel>

      <PageTitle title="Подписки" />

      <div className="row">
        <div className="col-12">
          <OrdersCardWrapper>
            <div className="card">
              <OrdersTabsWrapper>
                <ul className="nav nav-tabs-orders">
                  {SUBSCRIPTIONS_PAGE_TABS.map((tab) => (
                    <li className="nav-item" key={tab.id}>
                      <div
                        data-toggle="tab"
                        aria-expanded="true"
                        className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => handleOpenTab(tab)}
                      >
                        <div className="d-flex flex-row">
                          <div className="nav-link-icon">
                            <i className={tab.icon} />
                          </div>
                          <div className="tab-title">{tab.title}</div>
                          {/* {tab.states ?
                            <div>
                              {!statusesLoading ?
                                <span className="badge badge-pill">
																{!tab.problems ?
                                  getTabOrdersLength(tab,statusesLength)
                                  :
                                  statusesLength['PROBLEM_ORDERS']
                                }
															</span>
                                :
                                <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"/>
                              }
                            </div>
                          : null} */}
                        </div>
                      </div>
                      <div className={`nav-link-after ${activeTab === tab.id ? 'active' : ''}`} />
                    </li>
                  ))}
                </ul>
                <div className="card-body">
                  <FiltersRow
                    orders={orders}
                    search={search}
                    statusesLength={statusesLength}
                    selected={selected}
                    selectedLength={selectedLength}
                    showFilter={showFilter}
                    activeTab={activeTab}
                    unpackingOrders={unpackingOrders}
                    unpackingFilter={unpackingFilter}
                    packingControlOrders={packingControlOrders}
                    packingControlFilter={packingControlFilter}
                    loadingReport={loadingReport}
                    warehouses={warehouses}
                    activeWarehouse={activeWarehouse}
                    setActiveWarehouse={setActiveWarehouse}
                    handleSearch={handleSearch}
                    setShowFilter={setShowFilter}
                    hideAddNewButton
                    handleCloseFilter={handleCloseFilter}
                    handleSetFilters={handleSetFilters}
                    handleSetUnpackingFilter={handleSetUnpackingFilter}
                    handleSetControlPacking={handleSetControlPacking}
                    handleShippingOrders={handleShippingOrders}
                    handleNewOrder={handleNewOrder}
                    handleGetReport={handleGetReport}
                    updateOrders={updateOrders}
                    updateStatuses={updateStatuses}
                    history={history}
                    filterFormInitial={filterFormInitial}
                    setFilterFormInitial={setFilterFormInitial}
                    loading={loading}
                    filters={filters}
                    exportType="subscription"
                  />
                  <Wrapper>
                    <SpinnerBlock show={loading} />
                    <div className="fixed-table-container">
                      <div className="fixed-table-body">
                        <table className="table table-centered">
                          <thead className="thead-light">
                            <tr>
                              <th className="checkbox-td" onClick={handleToggleMainCheckbox}>
                                <Checkbox id={`checkbox-all`} checked={selectedAll} />
                              </th>
                              <Fragment>
                                {THEAD_ITEMS.map((title) => (
                                  <th>{title}</th>
                                ))}
                              </Fragment>
                            </tr>
                          </thead>

                          <tbody>
                            {filteredOrders.map((order) => (
                              <tr
                                key={order.id}
                                className={order.status === 'ORDER_INFO_CANCELED' ? 'canceled' : ''}
                              >
                                <td
                                  className="checkbox-td"
                                  onClick={() => handleToggleCheckbox(order.id)}
                                >
                                  <div className="mark-container">
                                    <div className="mark-wrapper">
                                      {order.tags
                                        ? order.tags
                                            .map((item) => (
                                              <span
                                                className="mark"
                                                style={{
                                                  background: TAG_COLORS.find(
                                                    (color) => color.id === item.color,
                                                  ).code,
                                                }}
                                              />
                                            ))
                                            .slice(0, 4)
                                        : null}
                                    </div>
                                    <div className="marks-list-wrapper">
                                      {order.tags && order.tags.length ? (
                                        <>
                                          {order.tags
                                            .sort((a, b) => (a.color > b.color ? 1 : -1))
                                            .map((tag) => {
                                              const currentColor = TAG_COLORS.find(
                                                (item) => item.id === tag.color,
                                              );
                                              return (
                                                <label
                                                  className="tag"
                                                  style={{
                                                    background: currentColor.code,
                                                    color: currentColor.color,
                                                  }}
                                                  onClick={(evt) => handleAddTag(evt, order)}
                                                >
                                                  {tag.name}
                                                  <i className="fe-edit-2" />
                                                </label>
                                              );
                                            })}
                                        </>
                                      ) : (
                                        <button
                                          className="add-mark"
                                          onClick={(evt) => handleAddTag(evt, order)}
                                        >
                                          <i className="fe-plus-circle" />
                                          Новая метка
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  {order.warehouseFFExportError ? (
                                    <span className="help-tooltip-wrapper">
                                      <span className="help-tooltip">
                                        {order.warehouseFFExportErrorComment}
                                      </span>
                                    </span>
                                  ) : null}
                                  <Checkbox
                                    id={`checkbox-${order.id}`}
                                    checked={selected[order.id]}
                                  />
                                </td>
                                <Link
                                  className="pseudo-td text-body"
                                  to={{
                                    pathname: `/orders/${order.id}`,
                                    state: {
                                      customBackLink: urlSearchStr
                                        ? '/subscriptions' +
                                          urlSearchStr +
                                          '&scroll=' +
                                          scrollPosition
                                        : null,
                                    },
                                  }}
                                >
                                  <div className="d-flex flex-column">
                                    <div
                                      className="order-td order-id-title"
                                      style={
                                        order.warehouseFFExportError
                                          ? { color: `#f1556c`, whiteSpace: `nowrap` }
                                          : undefined
                                      }
                                    >
                                      {order.id}
                                      {` `}
                                      {order.warehouseFFExportError ? (
                                        <i className="fe-alert-triangle" />
                                      ) : null}
                                    </div>
                                    <div style={{ minWidth: '85px' }}>
                                      <small
                                        className={order.warehouseFFExportError ? `` : `text-muted`}
                                        style={
                                          order.warehouseFFExportError
                                            ? { color: `#f1556c` }
                                            : undefined
                                        }
                                      >
                                        {getOrderDateString(order.date)}
                                      </small>
                                    </div>
                                  </div>
                                </Link>
                                <td>
                                  <ReceiverBlock row={order} />
                                </td>
                                <td>
                                  <StatusBar status={order.status} />
                                </td>
                                <td>
                                  <DeliveryBlock row={order} />
                                </td>
                                <td>
                                  <LocationBlock row={order} />
                                </td>
                                <td>
                                  <PaymentInfoBlock row={order} />
                                </td>
                                <td style={{ whiteSpace: 'nowrap' }}>{`${numeral(
                                  order.totalOrderSum,
                                ).format('0,0')} ${currencySymbol}`}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Wrapper>
                  <Pagination
                    total={totalItems}
                    currentPage={Number(page)}
                    onPageSelect={(pageIndex) => handlePageSelect(pageIndex)}
                  />
                </div>
              </OrdersTabsWrapper>
            </div>
          </OrdersCardWrapper>
        </div>
      </div>
    </>
  );
};

export default DesktopLayout;
